<template>
  <modal
    :close="close"
    :large="!modelIsScorecardGroup(model)"
    :xl="modelIsScorecardGroup(model)"
    title-border
  >
    <template #type>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center space-x-4">
          <span class="text-lg font-semibold text-gray-600">
            {{ model.name }}
          </span>
          <scorecard-id-badge :model-value="model.id" />
          <div v-if="modelIsScorecardGroup(model)">
            {{
              $t("SCORECARD_GROUPS.GPTS", {
                points: model?.group_points || "-",
              })
            }}
          </div>
        </div>
        <div class="flex items-center space-x-4">
          <scorecard-favorite-button
            v-if="!modelIsScorecardGroup(model) && !isReport"
            :model="model"
          />
          <scorecard-risk-tier-pill
            v-if="resultedRiskTier"
            :risk-tier="resultedRiskTier"
            readonly
          />
          <scorecard-status-pill
            v-if="resultedStatus"
            :status="resultedStatus"
            readonly
          />
        </div>
      </div>
    </template>
    <template #content>
      <div class="flex flex-col space-y-4 overflow-x-hidden p-5">
        <div v-if="getFunder(model.funder)" class="flex items-center space-x-4">
          <icon-base :icon="IconFunder" />
          <p class="text-headline">{{ getFunder(model.funder)?.full_name }}</p>
        </div>
        <p>{{ model.description }}</p>
        <div class="relative">
          <div
            v-if="!viewingGroupScorecard"
            class="rounded-t border-t border-r border-l bg-white px-5 py-8"
          >
            <scorecard-points-bar
              v-if="pageIsView"
              :categories="categories"
              :scorecards="isReport ? allGroupScorecards : null"
              :total="resultsPointsBarTotal"
              show-percentage
            />
            <scorecard-results-points-bar
              v-else
              show-percentage
              :categories="categories"
              :weighted-points="
                modelIsScorecardGroup(model)
                  ? model.results?.[0]?.weighted_points?.score_cards
                  : undefined
              "
              :total="resultsPointsBarTotal"
              :started="hasBeenStarted"
              :is-group="modelIsScorecardGroup(model)"
              :knocked-out="isKnockedOut"
            />
          </div>
          <template v-if="modelIsScorecardGroup(model)">
            <transition name="slide-fade-group">
              <lf-table
                v-show="!viewingGroupScorecard"
                :columns="groupColumns"
                :data="groupTableData"
                row-is-clickable
                :lazy-load="lazyLoadOptions"
                @row-clicked="viewScorecard"
              />
            </transition>
            <transition name="slide-fade-scorecard">
              <group-scorecard-details
                v-show="viewingGroupScorecard"
                :results-loading="scorecardGroupResultsLoading"
                :scorecard-group="model"
                :scorecards="allGroupScorecards"
                :scorecard="selectedGroupScorecard"
                :selected-scorecard-id="selectedGroupsScorecardId"
                :page-is-view="pageIsView"
                :is-report="isReport"
                :started="started"
                @back="viewingGroupScorecard = false"
                @select-scorecard="viewScorecard"
              />
            </transition>
          </template>
          <template v-else>
            <div class="grid grid-cols-7 border-b bg-gray-100 py-5 pl-6">
              <div
                v-for="title in resultTableTitles"
                :key="title"
                class="text-xs font-medium text-headline"
                :class="{ 'col-span-2': title === $t('SCORECARDS.ATTRIBUTES') }"
              >
                {{ title }}
              </div>
            </div>
            <div v-for="node in tableData" :key="node.key">
              <toggle-section
                v-if="node.children?.length"
                :section="node.key"
                chevron-position="left"
                :has-padding="false"
                :has-border="false"
                class="scorecard-data-table-row"
                :has-header-bottom-border="false"
                :is-open="isOpen[node.key] ?? true"
                @toggle="handleSectionToggle(node.key)"
              >
                <template #titleSection>
                  <scorecard-preview-modal-row
                    :is-report="isReport"
                    :node="node"
                    :model="model"
                    :page-is-view="pageIsView"
                  />
                </template>
                <template #content>
                  <scorecard-preview-modal-row
                    v-for="childNode in node.children"
                    :key="childNode.key"
                    :is-report="isReport"
                    :node="childNode"
                    :model="model"
                    :page-is-view="pageIsView"
                    class="border-b py-5 pl-10 last:border-0"
                  />
                </template>
              </toggle-section>
              <scorecard-preview-modal-row
                v-else
                class="border-b py-5 pl-10"
                :is-report="isReport"
                :node="node"
                :model="model"
                :page-is-view="pageIsView"
              />
            </div>
          </template>
          <div
            v-if="!modelIsScorecardGroup(model) || !viewingGroupScorecard"
            class="flex h-16 w-full items-center justify-between rounded-b bg-gray-100 p-6"
          >
            <lf-h4>
              {{ $t("SCORECARDS.TOTAL_PTS") }}
            </lf-h4>
            <lf-h4
              v-tooltip="{
                content:
                  isReport && modelIsScorecardGroup(model)
                    ? $t('SCORECARD_GROUPS.GPTS', { points: totalGroupPoints })
                    : null,
                placement: 'left',
              }"
            >
              <icon-base
                v-if="isKnockedOut"
                data-cy="knockout-triggered-points-modal"
                :icon="IconKnockout"
                :color="UTIL_COLORS.WARNING"
              />
              {{
                $t("SCORECARDS.PTS", {
                  points: totalPoints,
                })
              }}
            </lf-h4>
          </div>
        </div>
        <div
          v-if="modelIsScorecardGroup(model) && !!model.rules?.length"
          class="flex flex-col space-y-4"
        >
          <div
            class="flex-row-full-justify-between cursor-pointer"
            @click="isRulesOpen = !isRulesOpen"
          >
            <div class="flex space-x-2">
              <lf-h4>
                {{ $t("SCORECARD_GROUPS.RULES") }}
              </lf-h4>
              <span
                class="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-500"
              >
                {{ Number(model.rules.length) }}
              </span>
            </div>
            <div>
              <dropdown-chevron :is-open="isRulesOpen" />
            </div>
          </div>
          <div v-if="isRulesOpen" class="space-y-4">
            <group-rule-entry
              v-for="rule in model.rules || []"
              :key="rule.score_card_status_id"
              :group="model"
              :rule="rule"
              :max-width="false"
              readonly
            />
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import has from "lodash/has";
import isEmpty from "lodash/isEmpty";

import { convertToArray } from "@/helpers/common";
import { UTIL_COLORS } from "@/helpers/constants";
import { modelIsScorecardGroup } from "@/helpers/scorecards";
import { getScorecardIsKnockedOut } from "@/helpers/scorecards";
import { ScorecardsIsViewKey } from "@/helpers/symbols";
import { useDealsBase } from "@/hooks/deals";
import { mapCategoriesForTreeTable } from "@/hooks/scorecards";

import ToggleSection from "@/components/ToggleSection.vue";
import IconFunder from "@/components/icons/IconFunder.vue";
import IconKnockout from "@/components/icons/IconKnockout.vue";
import GroupRuleEntry from "@/components/scorecards/GroupRuleEntry.vue";
import ScorecardFavoriteButton from "@/components/scorecards/ScorecardFavoriteButton.vue";
import ScorecardIdBadge from "@/components/scorecards/ScorecardIdBadge.vue";
import ScorecardPointsBar from "@/components/scorecards/ScorecardPointsBar.vue";
import ScorecardPreviewModalRow from "@/components/scorecards/ScorecardPreviewModalRow.vue";
import ScorecardResultsPointsBar from "@/components/scorecards/ScorecardResultsPointsBar.vue";
import ScorecardRiskTierPill from "@/components/scorecards/ScorecardRiskTierPill.vue";
import ScorecardStatusPill from "@/components/scorecards/ScorecardStatusPill.vue";
import GroupScorecardDetails from "@/components/scorecards/scorecardPreviewModal/GroupScorecardDetails.vue";
import GroupPoints from "@/components/scorecards/scorecardPreviewModal/tableComponents/group/GroupPoints.vue";
import ScorecardKnockouts from "@/components/scorecards/scorecardPreviewModal/tableComponents/group/ScorecardKnockouts.vue";
import ScorecardName from "@/components/scorecards/scorecardPreviewModal/tableComponents/group/ScorecardName.vue";
import ScorecardPoints from "@/components/scorecards/scorecardPreviewModal/tableComponents/group/ScorecardPoints.vue";
import Modal from "@/components/ui/Modal.vue";
import LfTable from "@/components/ui/table/LfTable.vue";

import type { IDealsColumn } from "@/models/applications";
import type { FinancialProduct, LazyLoadOptions } from "@/models/common";
import type { ScorecardProductType } from "@/models/options";
import type {
  IScorecard,
  IScorecardGroup,
  IScorecardResult,
  IScorecardRiskTier,
  IScorecardStatus,
  MappedTreeTableCategory,
  ScorecardForGroupReportTable,
  ScorecardForGroupTable,
  ScorecardGroupPreview,
} from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
  model: {
    type: Object as PropType<IScorecard | IScorecardGroup>,
    required: true,
  },
  isReport: {
    type: Boolean,
    default: false,
  },
  started: {
    type: Boolean,
    default: false,
  },
});

const pageIsView = inject(ScorecardsIsViewKey, true);
const isRulesOpen = ref(true);
const scorecardGroupResultsLoading = ref(false);
const viewingGroupScorecard = ref(false);
const selectedGroupsScorecardId = ref<string>("");
const isOpen = ref<Record<string, boolean>>({});

const { t } = useI18n();
const { dispatch, getters } = useStore();
const { activeDeal } = useDealsBase();

const resultTableTitles = [
  t("SCORECARDS.SERVICE"),
  t("SCORECARDS.ATTRIBUTES"),
  t("SCORECARDS.KNOCKOUT"),
  t("SCORECARDS.VALUE"),
  t("SCORECARDS.DISTRIBUTION"),
  t("SCORECARDS.POINTS"),
];

const lazyLoadOptions: LazyLoadOptions = {
  style: {},
};

const formatProductType = (productTypeId: ScorecardProductType) =>
  productTypeId ? productTypes.value[productTypeId] : "-";

const groupColumns = computed<IDealsColumn[]>(() => {
  const columns = [
    {
      label: t("SCORECARDS.SCORECARD"),
      key: "name",
      headerClass: "w-80",
      component: ScorecardName,
    },
    {
      label: t("SCORECARDS.DISTRIBUTION"),
      key: "distribution",
      headerClass: "text-right",
      columnClass: "text-right",
      class: "text-xs",
    },
    {
      label: t("SCORECARDS.KNOCKOUT"),
      key: "knockout",
      component: props.isReport ? null : ScorecardKnockouts,
    },
    {
      label: t("SCORECARDS.SCORECARD_POINTS"),
      key: "scorecard_points",
      component: props.isReport ? null : ScorecardPoints,
    },
    {
      label: t("SCORECARDS.GROUP_POINTS"),
      key: "group_points",
      headerClass: "text-right",
      columnClass: "text-right",
      component: props.isReport ? null : GroupPoints,
    },
  ];

  const reportColumns = [
    {
      label: t("SCORECARDS.PROD_TYPE"),
      key: "product_type_id",
      headerClass: "w-50",
      formatter: formatProductType,
    },
    {
      label: t("SCORECARDS.RISK_TIER"),
      key: "score_card_risk_tier",
      headerClass: "w-40",
      component: ScorecardRiskTierPill,
      props: (model: IScorecardResult) => ({
        riskTier: model.score_card_risk_tier,
        "font-size": "text-xs",
        readonly: true,
      }),
    },
    {
      label: t("SCORECARDS.STATUS"),
      key: "score_card_status",
      headerClass: "w-30",
      component: ScorecardStatusPill,
      props: (model: IScorecardResult) => ({
        status: model.score_card_status,
        "font-size": "text-xs",
        readonly: true,
      }),
    },
  ];

  return props.isReport
    ? columns
    : [columns[0], ...reportColumns, ...columns.slice(1)];
});

const initSelectedScorecardId = () => {
  if (modelIsScorecardGroup(props.model)) {
    return props.model.score_cards[0].id;
  }

  return props.model.id;
};
const selectedScorecardId = ref(initSelectedScorecardId());

const selectedScorecard = computed(() => {
  if (modelIsScorecardGroup(props.model)) {
    return (
      props.model.score_cards.find(
        (scorecard) => scorecard.id === selectedScorecardId.value
      ) || null
    );
  }

  return props.model;
});

const productTypes = computed<Record<ScorecardProductType, FinancialProduct>>(
  () => getters["options/scorecardProductTypes"]
);

const selectedGroupScorecard = computed<ScorecardGroupPreview | null>(() => {
  if (!selectedGroupsScorecardId.value || !modelIsScorecardGroup(props.model)) {
    return null;
  }

  const scorecard = props.model.score_cards.find(
    (scorecard) => scorecard.id === selectedGroupsScorecardId.value
  );

  const scorecard_result = props.model.results?.[0]?.score_card_results?.find(
    (scorecard) => scorecard.score_card_id === selectedGroupsScorecardId.value
  );

  if (!scorecard) {
    return null;
  }

  return { ...scorecard, scorecard_result };
});

const allGroupScorecards = computed<ScorecardGroupPreview[] | null>(() => {
  if (!modelIsScorecardGroup(props.model)) {
    return null;
  }
  return props.model.score_cards.map((scorecard) => {
    const scorecard_result: IScorecardResult | undefined = (
      props.model as IScorecardGroup
    ).results?.[0]?.score_card_results?.find(
      (result: IScorecardResult) => result.score_card_id === scorecard.id
    );
    return { ...scorecard, scorecard_result };
  });
});

const hasBeenStarted = computed(
  () => !!props.model?.results?.length || props.isReport
);

const categories = computed(() => {
  if (!props.model.results?.length) {
    return selectedScorecard.value?.content?.categories || [];
  }

  return modelIsScorecardGroup(props.model)
    ? props.model.results?.[0]?.score_card_results?.find(
        (scr) => scr.score_card_id === selectedScorecard.value?.id
      )?.categories || []
    : selectedScorecard.value?.results?.[0]?.categories || [];
});

const resultsPointsBarTotal = computed(() => {
  if (!modelIsScorecardGroup(props.model)) {
    return selectedScorecard.value?.points || 0;
  }

  return props.model?.group_points || 0;
});

const isKnockedOut = computed(() => {
  const result = props.model?.results?.[0];
  if (modelIsScorecardGroup(props.model) || !result) {
    return false;
  }
  return getScorecardIsKnockedOut(result as IScorecardResult);
});

const tableData = ref<MappedTreeTableCategory[]>([]);

const groupTableData = computed<
  ScorecardForGroupTable[] | ScorecardForGroupReportTable[] | undefined
>(() => {
  if (!modelIsScorecardGroup(props.model)) {
    return [];
  }
  if (props.isReport) {
    const test = props.model.score_cards?.map((sc, index) => {
      return {
        ...sc,
        scorecard_points: getScorecardData(sc.id)?.points || 0,
        group_points: sc?.group_weight || 0,
        weighted_points: null,
        index,
        distribution: null,
      } as ScorecardForGroupReportTable;
    });
    return test;
  }
  return props.model.results?.[0]?.score_card_results?.map((sc, index) => {
    const weightedPoints = getWeightedPoints(sc);
    return {
      ...sc,
      id: sc.score_card_id,
      scorecard_points: getScorecardData(sc.score_card_id)?.points || 0,
      group_points: (props.model as IScorecardGroup)?.group_points || 0,
      weighted_points: weightedPoints,
      index,
      name: getScorecardData(sc.score_card_id)?.name || "",
      distribution: getDistribution(weightedPoints),
      product_type_id: getProductTypeId(sc.score_card_id),
    } as ScorecardForGroupTable;
  });
});

const isScorecardGroup = (
  model: IScorecard | IScorecardGroup
): model is IScorecardGroup => has(model, "score_cards");

const getProductTypeId = (screcardId: string) => {
  if (isScorecardGroup(props.model)) {
    return (
      props.model.score_cards.find((sc) => sc.id === screcardId)
        ?.product_type_id || null
    );
  }
  return null;
};

const viewScorecard = (id: number | string) => {
  if (!modelIsScorecardGroup(props.model)) {
    return;
  }
  const scorecardId = props.isReport
    ? props.model.score_cards.find((sc) => sc.id === id)?.id
    : props.model.results?.[0]?.score_card_results?.find(
        (sc) => sc.score_card_id === id
      )?.score_card_id;
  if (!scorecardId) {
    return;
  }

  selectedGroupsScorecardId.value = scorecardId;
  viewingGroupScorecard.value = true;
};

const getFunder = (funder: IScorecard["funder"]) => {
  const funders = convertToArray(funder);
  return funders.length > 0 ? funders[0] : null;
};

const getWeightedPoints = (sc: IScorecardResult): number => {
  if (!modelIsScorecardGroup(props.model)) {
    return 0;
  }
  return (
    props.model.results?.[0]?.weighted_points?.score_cards?.find(
      (wp_sc) => wp_sc.id === sc.score_card_id
    )?.weighted_points || 0
  );
};

const handleSectionToggle = (key: string) =>
  isOpen.value[key] === undefined
    ? (isOpen.value[key] = false)
    : (isOpen.value[key] = !isOpen.value[key]);

const getDistribution = (weightedPoints: number): string => {
  if (!modelIsScorecardGroup(props.model)) {
    return "-";
  }
  return (
    ((weightedPoints / (props.model?.group_points || 1)) * 100).toFixed(2) + "%"
  );
};

const getScorecardData = (id: string): IScorecard | undefined => {
  if (!modelIsScorecardGroup(props.model)) {
    return undefined;
  }
  return props.model.score_cards.find((sc) => sc.id === id);
};

const totalPoints = computed(() => {
  if (props.isReport) {
    return props.model?.points || 0;
  }
  const isScorecardGroup = modelIsScorecardGroup(props.model);
  let result;
  if (isScorecardGroup) {
    result =
      props.model?.results?.[0]?.weighted_points?.score_cards.reduce(
        (acc, curr) => {
          return acc + Number(curr.weighted_points || 0);
        },
        0
      ) || 0;
  } else {
    result = props.model?.results?.[0]?.points || 0;
  }
  const total =
    (isScorecardGroup ? props.model?.group_points : props.model?.points) || 0;
  const finalResult = isKnockedOut.value ? "" : result;
  return `${finalResult} ⁄ ${total}`;
});

const totalGroupPoints = computed(
  () =>
    ("group_points" in props.model && props.model?.group_points) ??
    (props.model?.points || 0)
);

const resultedRiskTier = computed(() => {
  let riskTier: IScorecardRiskTier | null = null;

  if (modelIsScorecardGroup(props.model)) {
    riskTier = props.model.results?.[0]?.score_card_group_risk_tier || null;
  } else {
    riskTier = props.model.results?.[0]?.score_card_risk_tier || null;
  }

  return riskTier;
});

const resultedStatus = computed(() => {
  let status: IScorecardStatus | null = null;

  if (modelIsScorecardGroup(props.model)) {
    status = props.model.results?.[0]?.score_card_group_status || null;
  } else {
    status = props.model.results?.[0]?.score_card_status || null;
  }

  return status;
});

const getExpandedKeys = (tableData: MappedTreeTableCategory[]) =>
  tableData.reduce(
    (acc, curr) => {
      if (curr.children?.length) {
        acc[curr.key] = !isEmpty(curr.data.backup_applied);
      }
      return acc;
    },
    {} as Record<string, boolean>
  );

const getScorecardGroupResultsForApplication = async () => {
  scorecardGroupResultsLoading.value = true;
  try {
    await dispatch("scorecards/getScorecardGroupResultsForApplication", {
      application_id: activeDeal.value?.id,
      id: props.model.id,
    });
  } finally {
    scorecardGroupResultsLoading.value = false;
  }
};

onMounted(() => {
  if (modelIsScorecardGroup(props.model) && !props.isReport) {
    // fetch results for group each time it is opened
    getScorecardGroupResultsForApplication();
  }
  tableData.value = mapCategoriesForTreeTable(
    categories.value,
    selectedScorecard.value,
    pageIsView,
    props.started
  );
  isOpen.value = getExpandedKeys(tableData.value);
});
</script>
<style>
@reference "@/assets/tailwind.css";

.p-treetable .p-treetable-tbody > tr.gray-row {
  @apply bg-disabled/50;
}
.attribute-node .attribute-node__tooltip::before {
  width: var(--width);
}

.slide-fade-scorecard-enter-active,
.slide-fade-scorecard-leave-active,
.slide-fade-group-enter-active,
.slide-fade-group-leave-active {
  transition: all 0.2s ease-out;
}

.slide-fade-scorecard-enter-from,
.slide-fade-scorecard-leave-to,
.slide-fade-group-enter-from,
.slide-fade-group-leave-to {
  @apply transform bg-white;
  opacity: 0;
  position: absolute;
  top: 0;
}

.slide-fade-scorecard-enter-from,
.slide-fade-scorecard-leave-to {
  @apply translate-x-full;
}

.slide-fade-group-enter-from,
.slide-fade-group-leave-to {
  @apply -translate-x-full transform;
}

.badge-class {
  @apply rounded border border-gray-700 px-1 text-xxxs leading-[14px] uppercase;
}

.scorecard-data-table-row .toggle-section__header {
  @apply -mx-4 border-b;
}
</style>
